import { ChangeDetectionStrategy, Component, Input, numberAttribute } from '@angular/core';

/** Stroke path data, 24x24 viewBox. Kept deliberately small — demo chrome only. */
const PATHS: Record<string, string[]> = {
  grid: ['M3 3h18v18H3z', 'M3 9h18M3 15h18M9 3v18M15 3v18'],
  keyboard: ['M2 6h20v12H2z', 'M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10'],
  upload: ['M12 16V4', 'M7 9l5-5 5 5', 'M4 20h16'],
  clock: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M12 7v5l3 2'],
  bookmark: ['M6 3h12v18l-6-4-6 4z'],
  followup: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M8 12l3 3 5-6'],
  snooze: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M9 9h6l-6 6h6'],
  template: ['M3 4h18v16H3z', 'M3 9h18M9 9v11'],
  archive: ['M3 6h18v4H3z', 'M5 10v10h14V10', 'M10 14h4'],
  trash: ['M4 7h16', 'M9 7V5h6v2', 'M6 7l1 13h10l1-13', 'M10 11v6M14 11v6'],
  folder: ['M3 6h6l2 2h10v11H3z'],
  knot: ['M8 8a4 4 0 108 0 4 4 0 10-8 0', 'M8 16a4 4 0 108 0 4 4 0 10-8 0'],
  tag: ['M3 3h8l10 10-8 8L3 11z', 'M7.5 7.5h.01'],
  chevron: ['M9 6l6 6-6 6'],
  chevrondown: ['M6 9l6 6 6-6'],
  plus: ['M12 5v14M5 12h14'],
  search: ['M11 19a8 8 0 100-16 8 8 0 000 16z', 'M21 21l-4.3-4.3'],
  filters: ['M4 6h16M7 12h10M10 18h4'],
  bell: ['M18 16V11a6 6 0 10-12 0v5l-2 3h16z', 'M10 22h4'],
  ai: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M9 13l2 2 4-5'],
  close: ['M6 6l12 12M18 6L6 18'],
  share: ['M4 12v8h16v-8', 'M12 16V4', 'M8 8l4-4 4 4'],
  dots: ['M6 12h.01M12 12h.01M18 12h.01'],
  box: ['M3 6h18v4H3z', 'M5 10v10h14V10'],
  stitch: ['M7 5v14M17 5v14', 'M7 9h10M7 15h10'],
  refresh: ['M4 12a8 8 0 0113.7-5.7L20 8', 'M20 4v4h-4', 'M20 12a8 8 0 01-13.7 5.7L4 16', 'M4 20v-4h4'],
  left: ['M15 6l-6 6 6 6'],
  right: ['M9 6l6 6-6 6'],
  mail: ['M3 6h18v12H3z', 'M3 7l9 6 9-6'],
  pin: ['M12 17v5', 'M9 3h6l-1 6 3 3H7l3-3z'],
  flag: ['M5 21V4h12l-2 4 2 4H5'],
  undo: ['M9 10H5V6', 'M5 10a8 8 0 1114 5'],
  redo: ['M15 10h4V6', 'M19 10a8 8 0 10-14 5'],
  painter: ['M4 5h12v5H4z', 'M16 7h3v5h-8v3', 'M10 15h2v5h-2z'],
  eraser: ['M8 20H4l9-9 6 6-3 3H8z', 'M11 8l6 6'],
  borders: ['M4 4h16v16H4z', 'M4 12h16M12 4v16'],
  merge: ['M4 6h16v5H4zM4 13h16v5H4z', 'M9 9l3 3 3-3'],
  align: ['M4 6h16M4 12h10M4 18h16'],
  // §11.6.3 #15–17: the icon shows the current value.
  'align-left': ['M4 6h16M4 12h10M4 18h14'],
  'align-center': ['M4 6h16M7 12h10M5 18h14'],
  'align-right': ['M4 6h16M10 12h10M6 18h14'],
  'valign-top': ['M4 4h16', 'M12 20V8', 'M8 12l4-4 4 4'],
  'valign-middle': ['M4 12h16', 'M12 3v5M9 5l3 3 3-3', 'M12 21v-5M9 19l3-3 3 3'],
  'valign-bottom': ['M4 20h16', 'M12 4v12', 'M8 12l4 4 4-4'],
  'wrap-overflow': ['M4 6v12', 'M8 12h12', 'M17 9l3 3-3 3'],
  'wrap-clip': ['M4 6v12M20 6v12', 'M8 12h8'],
  'wrap-wrap': ['M4 6h16M4 12h11a3 3 0 110 6h-3', 'M13 15l-2 3 2 3'],
  insert: ['M12 5v14M5 12h14'],
  wrap: ['M4 6h16M4 12h11a3 3 0 110 6h-3', 'M13 15l-2 3 2 3'],
  sigma: ['M6 5h12l-6 7 6 7H6'],
  func: ['M8 20V7a3 3 0 013-3h1', 'M6 11h8'],
  sort: ['M7 4v16l-3-3M7 20l3-3', 'M13 6h7M13 11h5M13 16h3'],
  filter: ['M4 5h16l-6 7v6l-4 2v-8z'],
  comment: ['M4 5h16v11H9l-5 4z'],
  check: ['M5 13l4 4L19 7'],
  table: ['M3 5h18v14H3z', 'M3 10h18M9 10v9'],
  image: ['M3 5h18v14H3z', 'M8 11a1.5 1.5 0 100-3 1.5 1.5 0 000 3', 'M21 16l-5-5-6 6'],
  link: ['M10 14a4 4 0 006 0l2-2a4 4 0 10-6-6l-1 1', 'M14 10a4 4 0 00-6 0l-2 2a4 4 0 106 6l1-1'],
  paint: ['M5 4h11v6H5z', 'M16 7h3v6h-7v3h-2v-5h6z'],
  sidebar: ['M3 4h18v16H3z', 'M15 4v16'],
  lab: ['M9 3v6l-5 9a2 2 0 002 3h12a2 2 0 002-3l-5-9V3', 'M9 3h6'],
  history: ['M12 21a9 9 0 10-9-9', 'M3 12l-1-3M3 12l3-1', 'M12 8v4l3 2'],
  download: ['M12 4v11', 'M8 11l4 4 4-4', 'M4 20h16'],
};

@Component({
  selector: 'app-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './icon.component.html',
  styles: ['svg { display: block; flex: none; }'],
})
export class IconComponent {
  @Input({ required: true }) public set name(value: string) {
    this.paths = PATHS[value] ?? [];
  }
  @Input({ transform: numberAttribute }) public size = 18;
  @Input({ transform: numberAttribute }) public weight = 1.6;

  protected paths: string[] = [];
}
